import React, { useState, useEffect, useCallback } from 'react';
import { membersAPI } from '../services/api';
import Swal from 'sweetalert2';

function Members() {
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    status: 'active'
  });

  const fetchMembers = useCallback(async () => {
    try {
      const response = await membersAPI.getAll({ search: searchTerm });
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSearch = () => {
    fetchMembers();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingMember) {
        await membersAPI.update(editingMember.id, formData);
        Swal.fire({
          icon: 'success',
          title: 'อัปเดตสำเร็จ!',
          text: 'ข้อมูลสมาชิกถูกอัปเดตแล้ว',
          confirmButtonColor: '#0ea5e9'
        });
      } else {
        const response = await membersAPI.create(formData);
        Swal.fire({
          icon: 'success',
          title: 'สร้างสมาชิกสำเร็จ!',
          html: `
            <div class="text-center">
              <p class="text-lg mb-2">รหัสสมาชิก</p>
              <p class="text-3xl font-bold text-primary-600">${response.data.member_code}</p>
            </div>
          `,
          confirmButtonColor: '#0ea5e9'
        });
      }

      setShowModal(false);
      setEditingMember(null);
      setFormData({ full_name: '', phone: '', email: '', status: 'active' });
      fetchMembers();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.error || 'ไม่สามารถบันทึกข้อมูลได้',
        confirmButtonColor: '#0ea5e9'
      });
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      full_name: member.full_name,
      phone: member.phone,
      email: member.email || '',
      status: member.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบ',
      text: 'คุณต้องการลบสมาชิกนี้ใช่หรือไม่?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก'
    });

    if (result.isConfirmed) {
      try {
        await membersAPI.delete(id);
        Swal.fire({
          icon: 'success',
          title: 'ลบสำเร็จ!',
          confirmButtonColor: '#0ea5e9'
        });
        fetchMembers();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'ลบไม่สำเร็จ',
          text: error.response?.data?.error || 'เกิดข้อผิดพลาด',
          confirmButtonColor: '#0ea5e9'
        });
      }
    }
  };

  const handleAdjustPoints = async (member) => {
    // Calculate days until January 1st reset
    const today = new Date();
    const nextJanuary1 = new Date(today.getFullYear() + 1, 0, 1);
    const daysUntilReset = Math.ceil((nextJanuary1 - today) / (1000 * 60 * 60 * 24));

    const result = await Swal.fire({
      title: 'ปรับคะแนนสมาชิก',
      html: `
        <div class="text-left mb-4">
          <p class="mb-2"><strong>สมาชิก:</strong> ${member.full_name}</p>
          <p class="mb-4"><strong>คะแนนปัจจุบัน:</strong> <span class="text-primary-600 font-bold">${member.points}</span> คะแนน</p>
          <p class="mb-2 text-sm text-orange-600">⚠️ คะแนนจะหมดอายุอีก ${daysUntilReset} วัน แล้วระบบจะรีเซ็ทเป็น 0</p>
        </div>
        <select id="transaction_type" class="swal2-input" style="display: block; width: 80%; margin: 10px auto;">
          <option value="earn">เพิ่มคะแนน</option>
          <option value="redeem">ลดคะแนน</option>
        </select>
        <input id="points" type="number" class="swal2-input" placeholder="จำนวนคะแนน" min="1" style="display: block; width: 80%; margin: 10px auto;">
        <textarea id="description" class="swal2-input" placeholder="หมายเหตุ (ไม่บังคับ)" style="display: block; width: 80%; margin: 10px auto; height: 60px;"></textarea>
      `,
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#0ea5e9',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
      didOpen: () => {
        // Focus on points input after modal opens
        setTimeout(() => {
          const pointsInput = document.getElementById('points');
          if (pointsInput) {
            pointsInput.focus();
          }
        }, 100);
      },
      preConfirm: () => {
        const transaction_type = document.getElementById('transaction_type').value;
        const points = parseInt(document.getElementById('points').value);
        const description = document.getElementById('description').value;

        if (!points || points <= 0) {
          Swal.showValidationMessage('กรุณาระบุจำนวนคะแนน');
          return false;
        }

        return { transaction_type, points, description };
      }
    });

    if (result.isConfirmed) {
      try {
        await membersAPI.updatePoints(member.id, result.value);
        Swal.fire({
          icon: 'success',
          title: 'ปรับคะแนนสำเร็จ!',
          confirmButtonColor: '#0ea5e9'
        });
        fetchMembers();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'ปรับคะแนนไม่สำเร็จ',
          text: error.response?.data?.error || 'เกิดข้อผิดพลาด',
          confirmButtonColor: '#0ea5e9'
        });
      }
    }
  };

  const viewHistory = async (member) => {
    try {
      const response = await membersAPI.getById(member.id);
      const memberData = response.data;

      const transactionsHTML = memberData.recent_transactions && memberData.recent_transactions.length > 0
        ? memberData.recent_transactions.map(t => `
            <tr>
              <td class="px-3 py-2 text-sm">${new Date(t.created_at).toLocaleDateString('th-TH')}</td>
              <td class="px-3 py-2 text-sm">
                <span class="px-2 py-1 rounded text-xs ${
                  t.transaction_type === 'earn' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }">${t.transaction_type === 'earn' ? 'เพิ่ม' : 'ลด'}</span>
              </td>
              <td class="px-3 py-2 text-sm font-bold ${t.transaction_type === 'earn' ? 'text-green-600' : 'text-red-600'}">
                ${t.transaction_type === 'earn' ? '+' : '-'}${t.points}
              </td>
              <td class="px-3 py-2 text-sm">${t.description || '-'}</td>
            </tr>
          `).join('')
        : '<tr><td colspan="4" class="text-center py-4 text-gray-500">ไม่มีประวัติ</td></tr>';

      Swal.fire({
        title: `ประวัติคะแนน - ${member.full_name}`,
        html: `
          <div class="text-left">
            <div class="bg-primary-50 p-4 rounded mb-4">
              <p class="mb-2"><strong>รหัสสมาชิก:</strong> <span class="font-mono">${memberData.member_code}</span></p>
              <p class="mb-2"><strong>คะแนนคงเหลือ:</strong> <span class="text-2xl font-bold text-primary-600">${memberData.points}</span> คะแนน</p>
              <p><strong>ยอดซื้อสะสม:</strong> ฿${memberData.total_spent?.toFixed(2)}</p>
            </div>
            <h4 class="font-bold mb-2">20 รายการล่าสุด</h4>
            <div class="max-h-96 overflow-y-auto">
              <table class="w-full text-left border">
                <thead class="bg-gray-100 sticky top-0">
                  <tr>
                    <th class="px-3 py-2 text-sm">วันที่</th>
                    <th class="px-3 py-2 text-sm">ประเภท</th>
                    <th class="px-3 py-2 text-sm">คะแนน</th>
                    <th class="px-3 py-2 text-sm">รายละเอียด</th>
                  </tr>
                </thead>
                <tbody class="divide-y">
                  ${transactionsHTML}
                </tbody>
              </table>
            </div>
          </div>
        `,
        width: '800px',
        confirmButtonColor: '#0ea5e9'
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลได้',
        confirmButtonColor: '#0ea5e9'
      });
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold">จัดการสมาชิก</h2>
        <button
          onClick={() => {
            setEditingMember(null);
            setFormData({ full_name: '', phone: '', email: '', status: 'active' });
            setShowModal(true);
          }}
          className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-6 rounded-lg flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          เพิ่มสมาชิกใหม่
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="ค้นหารหัสสมาชิก, ชื่อ, หรือเบอร์โทร..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            onClick={handleSearch}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 rounded-lg"
          >
            ค้นหา
          </button>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">รหัสสมาชิก</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ชื่อ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">เบอร์โทร</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">คะแนน</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ยอดซื้อสะสม</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">จัดการ</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {members.map((member) => (
              <tr key={member.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap font-mono text-sm font-bold text-primary-600">
                  {member.member_code}
                </td>
                <td className="px-6 py-4 whitespace-nowrap font-medium">{member.full_name}</td>
                <td className="px-6 py-4 whitespace-nowrap">{member.phone}</td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full font-bold inline-flex items-center gap-2 w-fit">
                      {member.points} คะแนน
                    </span>
                    {(() => {
                      // Calculate days until January 1st reset
                      const today = new Date();
                      const nextJanuary1 = new Date(today.getFullYear() + 1, 0, 1);
                      const daysUntilReset = Math.ceil((nextJanuary1 - today) / (1000 * 60 * 60 * 24));

                      if (daysUntilReset <= 30) {
                        return (
                          <span className="text-xs text-orange-600 font-semibold">
                            ⚠️ รีเซ็ทอีก {daysUntilReset} วัน
                          </span>
                        );
                      } else {
                        return (
                          <span className="text-xs text-gray-600">
                            รีเซ็ทอีก {daysUntilReset} วัน
                          </span>
                        );
                      }
                    })()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap font-bold">
                  ฿{member.total_spent?.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    member.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {member.status === 'active' ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => viewHistory(member)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                    title="ดูประวัติ"
                  >
                    📊
                  </button>
                  <button
                    onClick={() => handleAdjustPoints(member)}
                    className="text-green-600 hover:text-green-900 mr-3"
                    title="ปรับคะแนน"
                  >
                    ⚡
                  </button>
                  <button
                    onClick={() => handleEdit(member)}
                    className="text-primary-600 hover:text-primary-900 mr-3"
                    title="แก้ไข"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(member.id)}
                    className="text-red-600 hover:text-red-900"
                    title="ลบ"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">
              {editingMember ? 'แก้ไขสมาชิก' : 'เพิ่มสมาชิกใหม่'}
            </h3>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">ชื่อ-นามสกุล *</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">เบอร์โทร *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">อีเมล</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {editingMember && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">สถานะ</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="active">ใช้งาน</option>
                    <option value="inactive">ไม่ใช้งาน</option>
                  </select>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded"
                >
                  บันทึก
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingMember(null);
                    setFormData({ full_name: '', phone: '', email: '', status: 'active' });
                  }}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded"
                >
                  ยกเลิก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Members;
